import React from "react";
import {
  Table,
  Segment,
  Button,
  Header,
  Message,
  Divider,
} from "semantic-ui-react";
import { Form, Formik } from "formik";
import axios from "../../../../../../apis/dvdlocator";

const ReviewStep = ({
  inventory,
  supplyUnits,
  setSupplyUnits,
  onStepComplete,
  onPrevious,
}) => {
  const unitsToProcess = supplyUnits.filter(
    (unit) => unit.assign_new_inventory
  );

  // Group units by their new_inventory_id
  const groupedUnits = unitsToProcess.reduce((groups, unit) => {
    const groupId = unit.new_inventory_id;
    if (!groups[groupId]) {
      groups[groupId] = [];
    }
    groups[groupId].push(unit);
    return groups;
  }, {});

  const unchangedUnits = supplyUnits.filter(
    (unit) => !unit.assign_new_inventory
  );

  const formatInventoryEdits = (updatedSupplyUnits) => {
    const edits = [];

    // 1. Updating original inventory record inv
    const inv = unchangedUnits.reduce((acc, curr) => {
      return acc + (Number(curr.inv) || 0);
    }, 0);
    edits.push({ inventory_id: inventory.inventory_id, inv });

    // Group updated supply units by inventory_id
    const groupedUpdates = updatedSupplyUnits.reduce((acc, curr) => {
      if (!acc[curr.inventory_id]) {
        acc[curr.inventory_id] = {
          inventory_id: curr.inventory_id,
          inv: 0,
        };
      }

      // Find the original unit to get its inventory value
      const originalUnit = supplyUnits.find((unit) => unit.upc === curr.upc);
      if (originalUnit) {
        acc[curr.inventory_id].inv =
          Number(acc[curr.inventory_id].inv) + (Number(originalUnit.inv) || 0);
        acc[curr.inventory_id].shelf = originalUnit.new_inventory_shelf;
      }

      return acc;
    }, {});

    Object.values(groupedUpdates).forEach((update) => {
      // Ensure final values are numbers
      update.inv = Number(update.inv);
      edits.push(update);
    });

    return edits;
  };

  // Updated handleSubmit
  const handleSubmit = async (values, { setSubmitting }) => {
    const formattedLinkRequest = {
      supplyUnits: unitsToProcess.map((unit) => ({
        inventory_id: unit.new_inventory_id,
        upc: unit.upc,
        inventory_title: unit.new_inventory_title,
        part_number: inventory.part_number,
        retain_product_associations: true,
        previous_inventory_id: inventory.inventory_id,
      })),
    };

    try {
      const res = await axios.post(
        `/supply/link-inventory`,
        formattedLinkRequest
      );

      const updatedSupplyUnits = res.data.supplyUnits;

      const inventoryEdits = formatInventoryEdits(updatedSupplyUnits);

      for (const inventoryEdit of inventoryEdits) {
        const { inventory_id, ...editWithoutId } = inventoryEdit;
        await axios.put(`/inventory/${inventory_id}`, editWithoutId);
      }

      // Update local state with new inventory IDs
      setSupplyUnits((prev) =>
        prev.map((unit) => {
          const updatedUnit = updatedSupplyUnits.find(
            (u) => u.upc === unit.upc
          );

          if (!updatedUnit) return unit;

          return {
            ...unit,
            new_inventory_id: updatedUnit.inventory_id,
          };
        })
      );
    } catch (err) {
      console.log(err);
    }

    // Call the callback to proceed with save
    onStepComplete({
      supplyUnits,
      groupedUnits,
    });
    setSubmitting(false);
  };

  // Calculate total inventory for a group
  const calculateTotalInventory = (units) => {
    return units.reduce((sum, unit) => sum + (unit.inv || 0), 0);
  };

  return (
    <Formik
      initialValues={{
        confirmChanges: false,
      }}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form>
          <Segment>
            {/* Summary header */}
            <Header as="h3">Review Inventory Changes</Header>
            <p>Please review the following changes before submitting:</p>

            {/* Remaining items section - with gray background */}
            <Segment>
              <Header as="h4">Remaining in Original Inventory</Header>
              {unchangedUnits.length === 0 ? (
                <Message info>
                  <p>All items will be moved to new inventory IDs.</p>
                </Message>
              ) : (
                <Table celled compact>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>UPC</Table.HeaderCell>
                      <Table.HeaderCell>Title</Table.HeaderCell>
                      <Table.HeaderCell>Inventory</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {unchangedUnits.map((unit, index) => (
                      <Table.Row key={`${unit.upc}-${index}`}>
                        <Table.Cell>{unit.upc}</Table.Cell>
                        <Table.Cell>{unit.supply_title}</Table.Cell>
                        <Table.Cell>{unit.inv || 0}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}
            </Segment>

            <Divider />

            {/* New inventory groups - with visual emphasis */}
            <Header
              as="h4"
              color="blue"
              style={{ fontSize: "1.4em", margin: "1.5em 0 1em" }}
            >
              New Inventory Assignments
            </Header>
            {Object.keys(groupedUnits).length === 0 ? (
              <Message info>
                <p>No items were selected for new inventory assignment.</p>
              </Message>
            ) : (
              Object.entries(groupedUnits).map(([groupId, units]) => {
                // Get the title and shelf from the first unit in the group
                // since all units in a group have the same title and shelf
                const groupTitle =
                  units[0]?.new_inventory_title ||
                  `Group ${Math.abs(parseInt(groupId))}`;
                const groupShelf = units[0]?.new_inventory_shelf || "";

                // Calculate total inventory for this group
                const totalInventory = calculateTotalInventory(units);

                return (
                  <Segment
                    key={groupId}
                    raised
                    padded
                    style={{
                      borderLeft: "4px solid #2185d0",
                      backgroundColor:
                        "#FFFAF3" /* Very subtle yellow/cream background */,
                    }}
                  >
                    <Header as="h5" color="blue">
                      Group {Math.abs(parseInt(groupId))}
                    </Header>
                    <Header as="h3">Title: {groupTitle}</Header>
                    <p>Shelf: {groupShelf}</p>
                    <p>Total Inventory: {totalInventory}</p>
                    <Table celled compact>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>UPC</Table.HeaderCell>
                          <Table.HeaderCell>Title</Table.HeaderCell>
                          <Table.HeaderCell>Inventory</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {units.map((unit, index) => (
                          <Table.Row key={`${unit.upc}-${index}`}>
                            <Table.Cell>{unit.upc}</Table.Cell>
                            <Table.Cell>{unit.supply_title}</Table.Cell>
                            <Table.Cell>{unit.inv || 0}</Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </Segment>
                );
              })
            )}

            {/* Action buttons */}
            <div
              style={{
                marginTop: "20px",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Button type="button" onClick={onPrevious}>
                Back
              </Button>
              <Button type="submit" primary disabled={isSubmitting}>
                Submit Changes
              </Button>
            </div>
          </Segment>
        </Form>
      )}
    </Formik>
  );
};

export default ReviewStep;
